import { CSSProperties } from "react";
import { IWebsiteBuilderNodeDesignStyles, TWebsiteBuilderNodeDatasetFromModel } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderSectionEntityStretch, websiteBuilderGenerateDefaultSectionEntityStretch } from "../websiteBuilderSectionEntity";
import { IWebsiteBuilderContainerBase, IWebsiteBuilderContainerDataBase } from "./websiteBuilderContainer";
import { websiteBuilderNormalizeDatabaseId, websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { clamp } from "lodash";

export const WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES = 10;
export const WEBSITE_BUILDER_SLIDESHOW_MIN_TIME_PER_SLIDE = 1000;
export const WEBSITE_BUILDER_SLIDESHOW_MAX_TIME_PER_SLIDE = 10000;
export const WEBSITE_BUILDER_SLIDESHOW_MIN_BORDER_RADIUS = 0;
export const WEBSITE_BUILDER_SLIDESHOW_MAX_BORDER_RADIUS = 100;

export interface IWebsiteBuilderSlideshowImage {
  mediaId: number;
  src: string;
  alt: string;
}

export type TWebsiteBuilderSlideshowObjectFit = "cover" | "contain" | "fill";

function isWebsiteBuilderSlideshowObjectFit(value: string): value is TWebsiteBuilderSlideshowObjectFit {
  return value === "cover" || value === "contain" || value === "fill";
}

export interface IWebsiteBuilderSlideshowSettings {
  images: IWebsiteBuilderSlideshowImage[];
  objectFit: TWebsiteBuilderSlideshowObjectFit;
  borderRadius: number;
  autoplay: boolean;
  timePerSlide: number;
  showArrows: boolean;
  showPagination: boolean;
  expandImageOnClick: boolean;
}

type TWebsiteBuilderSlideshowDatasetModel = Pick<IWebsiteBuilderSlideshowSettings, "autoplay" | "timePerSlide"> & {
  activeSlideIndex: number;
  transitioning: boolean;
};
export type TWebsiteBuilderSlideshowDataset = TWebsiteBuilderNodeDatasetFromModel<TWebsiteBuilderSlideshowDatasetModel>;

export interface IWebsiteBuilderSlideshowData extends IWebsiteBuilderContainerDataBase {
  type: "slideshow";
  settings: IWebsiteBuilderSlideshowSettings;
  stretch: IWebsiteBuilderSectionEntityStretch;
}

export interface IWebsiteBuilderSlideshow extends IWebsiteBuilderContainerBase<[]> {
  container: IWebsiteBuilderSlideshowData;
}

export function isWebsiteBuilderSlideshow(node: any): node is IWebsiteBuilderSlideshow {
  return node?.type === "container" && node.container?.type === "slideshow";
}

export function websiteBuilderGenerateDefaultSlideshow(): IWebsiteBuilderSlideshow {
  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "slideshow",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 400, height: 400 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: 200 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultSlideshowSettings(),
      stretch: websiteBuilderGenerateDefaultSectionEntityStretch(),
    },
    children: [],
  };
}

function generateDefaultSlideshowSettings(): IWebsiteBuilderSlideshowSettings {
  return {
    images: [],
    objectFit: "cover",
    borderRadius: WEBSITE_BUILDER_SLIDESHOW_MIN_BORDER_RADIUS,
    autoplay: false,
    timePerSlide: 3000,
    showArrows: true,
    showPagination: true,
    expandImageOnClick: true,
  };
}

export function websiteBuilderNormalizeSlideshowSettings(
  partialSettings: Partial<IWebsiteBuilderSlideshowSettings>,
): IWebsiteBuilderSlideshowSettings {
  const defaultSettings = generateDefaultSlideshowSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const {
    images,
    objectFit,
    borderRadius,
    autoplay,
    timePerSlide,
    showArrows,
    showPagination,
    expandImageOnClick,
  } = partialSettings;

  let normalizedImages: IWebsiteBuilderSlideshowImage[] = defaultSettings.images;

  if (images?.length > 0) {
    normalizedImages = images.slice(0, WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES).map(image => {
      return {
        mediaId: websiteBuilderNormalizeDatabaseId(image?.mediaId, null, false),
        src: websiteBuilderNormalizeValue({
          type: "string",
          value: image?.src,
          defaultValue: null,
          required: false,
        }),
        alt: websiteBuilderNormalizeValue({
          type: "string",
          value: image?.alt,
          defaultValue: "",
          required: true,
        }),
      };
    });
  }

  const normalizedSettings: IWebsiteBuilderSlideshowSettings = {
    images: normalizedImages,
    objectFit: websiteBuilderNormalizeValue({
      type: "string",
      value: objectFit,
      defaultValue: defaultSettings.objectFit,
      required: true,
      validate: isWebsiteBuilderSlideshowObjectFit,
    }) as TWebsiteBuilderSlideshowObjectFit,
    borderRadius: websiteBuilderNormalizeValue({
      type: "number",
      value: borderRadius,
      defaultValue: defaultSettings.borderRadius,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_SLIDESHOW_MIN_BORDER_RADIUS,
        WEBSITE_BUILDER_SLIDESHOW_MAX_BORDER_RADIUS,
      ),
    }),
    autoplay: websiteBuilderNormalizeValue({
      type: "boolean",
      value: autoplay,
      defaultValue: defaultSettings.autoplay,
      required: true,
    }),
    timePerSlide: websiteBuilderNormalizeValue({
      type: "number",
      value: timePerSlide,
      defaultValue: defaultSettings.timePerSlide,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_SLIDESHOW_MIN_TIME_PER_SLIDE,
        WEBSITE_BUILDER_SLIDESHOW_MAX_TIME_PER_SLIDE,
      ),
    }),
    showArrows: websiteBuilderNormalizeValue({
      type: "boolean",
      value: showArrows,
      defaultValue: defaultSettings.showArrows,
      required: true,
    }),
    showPagination: websiteBuilderNormalizeValue({
      type: "boolean",
      value: showPagination,
      defaultValue: defaultSettings.showPagination,
      required: true,
    }),
    expandImageOnClick: websiteBuilderNormalizeValue({
      type: "boolean",
      value: expandImageOnClick,
      defaultValue: defaultSettings.expandImageOnClick,
      required: true,
    }),
  };

  return normalizedSettings;
}

interface IWebsiteBuilderSlideshowDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  contentClassName: string;
  slideTrackClassName: string;
  slideClassName: string;
  backgroundImageClassName: string;
  imageClassName: string;
  leftChevronClassName: string;
  rightChevronClassName: string;
  paginationClassName: string;
  dotClassName: string;
  dotActiveClassName: string;
}

export function websiteBuilderGenerateSlideshowDesignStyles(settings: IWebsiteBuilderSlideshowSettings): IWebsiteBuilderSlideshowDesignStyles {
  const styleProperties: CSSProperties = {
    borderRadius: settings.borderRadius,
  };

  let styleDeclaration = "";

  if (styleProperties.borderRadius != null) {
    styleDeclaration += `border-radius: ${styleProperties.borderRadius}px;`;
  }

  return {
    className: `website-builder-slideshow website-builder-slideshow-object-fit-${settings.objectFit}`,
    styleProperties,
    styleDeclaration,
    contentClassName: "website-builder-slideshow-content",
    slideTrackClassName: "website-builder-slideshow-slide-track",
    slideClassName: "website-builder-slideshow-slide",
    backgroundImageClassName: "website-builder-slideshow-background-image",
    imageClassName: "website-builder-slideshow-image",
    leftChevronClassName: "website-builder-slideshow-left-chevron",
    rightChevronClassName: "website-builder-slideshow-right-chevron",
    paginationClassName: "website-builder-slideshow-pagination",
    dotClassName: "website-builder-slideshow-dot",
    dotActiveClassName: "website-builder-slideshow-dot-active",
  };
}
